import { useEffect, useState, useCallback } from "react";
import {
  PackageCheck,
  Calendar,
  Search,
  RefreshCw,
  Trash2,
  Building2,
  User,
  Phone,
  AlertCircle,
  TrendingUp,
  PieChart as PieChartIcon,
  BarChart3,
  MapPin,
  Filter,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  ProductEnquiryService,
  ProductEnquiryItem,
  ProductEnquiryAnalyticsData,
} from "../../services/api/productEnquiry.service";
import { OrderService } from "../../services/api/order.service";
import { DataTable } from "../DataTable/DataTable";

interface ClinicOption {
  id: string;
  name: string;
}

export function ProductEnquiryAnalyticsView() {
  const [loadingDashboard, setLoadingDashboard] = useState<boolean>(true);
  const [loadingListing, setLoadingListing] = useState<boolean>(true);
  const [analyticsData, setAnalyticsData] = useState<ProductEnquiryAnalyticsData | null>(null);
  const [enquiries, setEnquiries] = useState<ProductEnquiryItem[]>([]);
  const [errorListing, setErrorListing] = useState<string | null>(null);

  // Clinic options for filter
  const [clinicList, setClinicList] = useState<ClinicOption[]>([]);

  // Filters & Pagination
  const [selectedClinicId, setSelectedClinicId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 1,
    totalItemsCount: 0,
  });

  // Delete modal state
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [itemToDelete, setItemToDelete] = useState<ProductEnquiryItem | null>(null);

  // Load clinic dropdown options
  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const res = await OrderService.getClinicList();
        if (res && res.data) {
          setClinicList(res.data);
        }
      } catch (err) {
        console.error("Failed to load clinic list:", err);
      }
    };
    fetchClinics();
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const res = await ProductEnquiryService.getAnalytics({
        clinic_id: selectedClinicId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (res.success && res.data) {
        setAnalyticsData(res.data);
      } else {
        toast.error(res.message || "Failed to load analytics metrics");
      }
    } catch (err) {
      console.error("Error loading analytics:", err);
    } finally {
      setLoadingDashboard(false);
    }
  }, [selectedClinicId, startDate, endDate]);

  const fetchListing = useCallback(async () => {
    setLoadingListing(true);
    setErrorListing(null);
    try {
      const res = await ProductEnquiryService.getEnquiries({
        page: pagination.page,
        pageSize: pagination.pageSize,
        clinic_id: selectedClinicId || undefined,
        search: searchQuery.trim() || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (res.success && Array.isArray(res.data)) {
        setEnquiries(res.data);
        if (res.pagination) {
          setPagination((prev) => ({
            ...prev,
            totalPages: res.pagination.totalPages || 1,
            totalItemsCount: res.pagination.totalItemsCount || res.data.length,
          }));
        }
      } else {
        setErrorListing(res.message || "Failed to load product enquiries");
        setEnquiries([]);
      }
    } catch (err) {
      console.error("Error loading enquiries listing:", err);
      setErrorListing("An unexpected error occurred while loading enquiries.");
      setEnquiries([]);
    } finally {
      setLoadingListing(false);
    }
  }, [pagination.page, pagination.pageSize, selectedClinicId, searchQuery, startDate, endDate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const handleRefresh = () => {
    fetchAnalytics();
    fetchListing();
  };

  const handleDelete = async (id: number) => {
    try {
      setDeletingId(id);
      const res = await ProductEnquiryService.deleteEnquiry(id);
      if (res.success) {
        toast.success(res.message || "Product enquiry deleted successfully");
        setItemToDelete(null);
        handleRefresh();
      } else {
        toast.error(res.message || "Failed to delete enquiry");
      }
    } catch (err) {
      toast.error("Error deleting product enquiry");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    {
      key: "clinic_details",
      title: "Clinic Info",
      render: (item: ProductEnquiryItem) => (
        <div className="space-y-1">
          <div className="flex items-center space-x-1.5 font-semibold text-gray-900 text-sm">
            <Building2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span>{item.clinic_details?.name || item.clinic_id}</span>
          </div>
          {item.clinic_details?.address && (
            <div className="flex items-center text-xs text-gray-500 truncate max-w-xs">
              <MapPin className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" />
              <span className="truncate">{item.clinic_details.address}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "enquiry_item",
      title: "Enquired Product",
      render: (item: ProductEnquiryItem) => (
        <div className="flex items-center space-x-2">
          <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-md flex-shrink-0">
            <PackageCheck className="w-4 h-4" />
          </div>
          <span className="font-semibold text-gray-900 text-sm">{item.enquiry_item}</span>
        </div>
      ),
    },
    {
      key: "patient_name",
      title: "Patient Details",
      render: (item: ProductEnquiryItem) => (
        <div className="space-y-0.5">
          <div className="flex items-center text-xs text-gray-800 font-medium">
            <User className="w-3.5 h-3.5 text-gray-400 mr-1.5" />
            {item.patient_name || <span className="text-gray-400 italic">Anonymous</span>}
          </div>
          {item.patient_mobile_number && (
            <div className="flex items-center text-xs text-gray-500">
              <Phone className="w-3.5 h-3.5 text-gray-400 mr-1.5" />
              {item.patient_mobile_number}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "created_at",
      title: "Enquiry Date",
      render: (item: ProductEnquiryItem) => (
        <div className="flex items-center text-xs text-gray-600">
          <Calendar className="w-3.5 h-3.5 text-gray-400 mr-1.5" />
          {formatDate(item.created_at)}
        </div>
      ),
    },
    {
      key: "action",
      title: "Action",
      width: "w-[90px]",
      render: (item: ProductEnquiryItem) => (
        <button
          onClick={() => setItemToDelete(item)}
          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete Enquiry"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Top Header & Sub Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-emerald-600" />
            Enquiry Analytics
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track user search trends, product demand, and clinic product enquiries.
          </p>
        </div>

        <button
          onClick={handleRefresh}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium text-sm rounded-xl shadow-xs transition-colors self-start sm:self-auto"
        >
          <RefreshCw className={`w-4 h-4 ${loadingDashboard || loadingListing ? "animate-spin text-emerald-600" : ""}`} />
          Refresh Data
        </button>
      </div>

      {/* Analytics Sub-Tabs Navigation */}
      {/* <AnalyticsSubTabs activeTab="product-enquiry" /> */}

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Enquiries */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Enquiries</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1">
              {loadingDashboard ? "..." : analyticsData?.totalEnquiries ?? 0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Overall product enquiries</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <PackageCheck className="w-6 h-6" />
          </div>
        </div>

        {/* Today's Enquiries */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Today's Enquiries</p>
            <h3 className="text-2xl font-bold text-blue-600 mt-1">
              {loadingDashboard ? "..." : analyticsData?.todayEnquiries ?? 0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Submitted today</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Calendar className="w-6 h-6" />
          </div>
        </div>

        {/* Patient Provided Count */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Patient Linked</p>
            <h3 className="text-2xl font-bold text-purple-600 mt-1">
              {loadingDashboard ? "..." : analyticsData?.patientProvidedCount ?? 0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Patient contact details attached</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
            <User className="w-6 h-6" />
          </div>
        </div>

        {/* Anonymous Count */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Anonymous</p>
            <h3 className="text-2xl font-bold text-amber-600 mt-1">
              {loadingDashboard ? "..." : analyticsData?.anonymousCount ?? 0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">General stock enquiries</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <PieChartIcon className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Analytics Breakdown Charts / Lists Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Inquired Products */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200/80 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              Top Inquired Products
            </h3>
            <span className="text-xs text-gray-400">Most requested items</span>
          </div>

          {loadingDashboard ? (
            <div className="p-8 text-center text-xs text-gray-500">Loading top products...</div>
          ) : analyticsData?.topEnquiryItems && analyticsData.topEnquiryItems.length > 0 ? (
            <div className="space-y-3">
              {analyticsData.topEnquiryItems.slice(0, 6).map((item, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex justify-between text-xs font-medium text-gray-800">
                    <span className="truncate max-w-[240px]">{item.enquiry_item}</span>
                    <span className="text-gray-500">
                      {item.count} enquiries ({Number(item.percentage || 0).toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div
                      className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, item.percentage || 0)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-xs text-gray-400">No top enquiry data available</div>
          )}
        </div>

        {/* Enquiries by Clinic */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200/80 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Enquiries by Clinic
            </h3>
            <span className="text-xs text-gray-400">Clinic-wise demand</span>
          </div>

          {loadingDashboard ? (
            <div className="p-8 text-center text-xs text-gray-500">Loading clinic metrics...</div>
          ) : analyticsData?.enquiriesByClinic && analyticsData.enquiriesByClinic.length > 0 ? (
            <div className="space-y-3">
              {analyticsData.enquiriesByClinic.slice(0, 6).map((clinic, idx) => {
                const total = analyticsData.totalEnquiries || 1;
                const percentage = ((clinic.count / total) * 100).toFixed(1);
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex justify-between text-xs font-medium text-gray-800">
                      <span className="truncate max-w-[240px]">
                        {clinic.clinic_details?.name || clinic.clinic_id}
                      </span>
                      <span className="text-gray-500">
                        {clinic.count} enquiries ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(100, Number(percentage))}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-xs text-gray-400">No clinic enquiry data available</div>
          )}
        </div>
      </div>

      {/* Filter Options Bar for Enquiries Listing */}
      <div className="bg-white p-4 rounded-xl border border-gray-200/80 shadow-xs space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <Filter className="w-4 h-4 text-emerald-600" />
            Filter Product Enquiries
          </h3>
          {(searchQuery || selectedClinicId || startDate || endDate) && (
            <button
              onClick={() => {
                setSearchQuery("");
                setSelectedClinicId("");
                setStartDate("");
                setEndDate("");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search product or patient..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full pl-9 pr-3 py-2 bg-gray-50/50 border border-gray-200 rounded-lg text-xs text-gray-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
            />
          </div>

          {/* Clinic Dropdown */}
          <select
            value={selectedClinicId}
            onChange={(e) => {
              setSelectedClinicId(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="w-full px-3 py-2 bg-gray-50/50 border border-gray-200 rounded-lg text-xs text-gray-700 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
          >
            <option value="">All Clinics</option>
            {clinicList.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {/* Start Date */}
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="w-full px-3 py-2 bg-gray-50/50 border border-gray-200 rounded-lg text-xs text-gray-700 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
          />

          {/* End Date */}
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="w-full px-3 py-2 bg-gray-50/50 border border-gray-200 rounded-lg text-xs text-gray-700 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
          />
        </div>
      </div>

      {/* Main Enquiries DataTable Card */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
        {errorListing ? (
          <div className="p-8 text-center space-y-3">
            <div className="inline-flex p-3 bg-red-50 text-red-500 rounded-full">
              <AlertCircle className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-gray-800">{errorListing}</p>
            <button
              onClick={fetchListing}
              className="px-4 py-2 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Retry Loading
            </button>
          </div>
        ) : (
          <DataTable
            data={enquiries}
            columns={columns}
            isLoading={loadingListing}
            pagination={{
              currentPage: pagination.page,
              totalPages: pagination.totalPages,
              totalItemsCount: pagination.totalItemsCount,
              pageSize: pagination.pageSize,
              setCurrentPage: (page) => setPagination((prev) => ({ ...prev, page })),
              setPageSize: (pageSize) => setPagination((prev) => ({ ...prev, pageSize, page: 1 })),
            }}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4 shadow-xl border border-gray-100">
            <div className="flex items-center space-x-3 text-red-600">
              <div className="p-2 bg-red-50 rounded-lg">
                <Trash2 className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Product Enquiry</h3>
            </div>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete the enquiry for{" "}
              <strong className="text-gray-900">{itemToDelete.enquiry_item}</strong> from clinic{" "}
              <strong className="text-gray-900">{itemToDelete.clinic_details?.name || itemToDelete.clinic_id}</strong>?
            </p>
            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                onClick={() => setItemToDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(itemToDelete.id)}
                disabled={deletingId === itemToDelete.id}
                className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {deletingId === itemToDelete.id ? "Deleting..." : "Delete Enquiry"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductEnquiryAnalyticsView;
